import { JobCardForm } from "@/pages/jobcards/types";

interface Props {
  form: JobCardForm;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onPreview: () => void;
  saving: boolean;
  saved: boolean;
}

const statusColors: Record<string, { bg: string; text: string }> = {
  Pending:       { bg: "#fffbeb", text: "#d97706" },
  "In Progress": { bg: "#eff6ff", text: "#2563eb" },
  Completed:     { bg: "#f0fdf4", text: "#16a34a" },
  Delivered:     { bg: "#f5f3ff", text: "#7c3aed" },
};

function completionScore(form: JobCardForm): number {
  const checks = [
    !!form.projectTitle,
    !!form.surveyLocation,
    !!form.clientId,
    !!form.assignedDate,
    !!form.completionDate,
    form.services.length > 0,
    form.fieldTeam.length > 0,
    !!form.scopeOfWork,
    form.deliverables.length > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export default function JobCardSidebar({ form, onSaveDraft, onSubmit, onPreview, saving, saved }: Props) {
  const score = completionScore(form);
  const sc = statusColors[form.currentStatus] || statusColors["Pending"];

  const checks = [
    { label: "Project Title", done: !!form.projectTitle },
    { label: "Survey Location", done: !!form.surveyLocation },
    { label: "Client Selected", done: !!form.clientId },
    { label: "Assigned Date", done: !!form.assignedDate },
    { label: "Completion Date", done: !!form.completionDate },
    { label: "Service Type(s)", done: form.services.length > 0 },
    { label: "Field Team", done: form.fieldTeam.length > 0 },
    { label: "Scope of Work", done: !!form.scopeOfWork },
    { label: "Deliverables", done: form.deliverables.length > 0 },
  ];

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-2.5">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Actions</h4>

        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || score < 60}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-all"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`${saving ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} text-sm`} />
          </span>
          {saving ? "Saving..." : "Submit Job Card"}
        </button>

        <button
          type="button"
          onClick={onSaveDraft}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-save-line text-sm" />
          </span>
          Save as Draft
        </button>

        <button
          type="button"
          onClick={onPreview}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-all"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-eye-line text-sm" />
          </span>
          Preview / Print
        </button>

        {saved && (
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-amber-50 border border-amber-100">
            <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-circle-fill text-amber-500 text-sm" />
            </span>
            <span className="text-xs text-amber-700 font-medium">Saved successfully!</span>
          </div>
        )}
      </div>

      {/* Completion Score */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Form Completion</h4>
          <span className={`text-sm font-bold ${score === 100 ? "text-amber-600" : score >= 60 ? "text-amber-600" : "text-red-500"}`}>
            {score}%
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${score}%`,
              background: score === 100 ? "#10b981" : score >= 60 ? "#f59e0b" : "#ef4444",
            }}
          />
        </div>
        <div className="space-y-1.5">
          {checks.map((c) => (
            <div key={c.label} className="flex items-center gap-2">
              <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${c.done ? "bg-amber-500" : "bg-gray-200"}`}>
                {c.done && <i className="ri-check-line text-white text-xs" />}
              </span>
              <span className={`text-xs ${c.done ? "text-gray-700" : "text-gray-400"}`}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Info */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Info</h4>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Job Number</span>
            <span className="text-xs font-bold text-gray-800 font-mono">{form.jobNo || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Status</span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>
              {form.currentStatus}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Priority</span>
            <span className="text-xs font-semibold text-gray-700">{form.priority || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Services</span>
            <span className="text-xs font-semibold text-gray-700">{form.services.length} selected</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Field Team</span>
            <span className="text-xs font-semibold text-gray-700">{form.fieldTeam.length} members</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Due Date</span>
            <span className="text-xs font-semibold text-gray-700">{form.completionDate || "—"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Created</span>
            <span className="text-xs text-gray-500">{form.createdDate || "—"}</span>
          </div>
        </div>
      </div>

      {/* Status History */}
      {form.statusTimeline.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Status History</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {[...form.statusTimeline].reverse().map((entry, i) => {
              const c = statusColors[entry.status] || statusColors["Pending"];
              return (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: c.text }} />
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold" style={{ color: c.text }}>{entry.status}</span>
                      <span className="text-xs text-gray-400">{entry.date}</span>
                    </div>
                    <p className="text-xs text-gray-600">{entry.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
